import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from '#icons';
import { type DateRange, type PreviousPeriods } from '../../types';
import { PeriodButton } from '../styles';
import { isSameRange } from '../../helpers/isSameDateRange';
import { QuickRangeWrapper, PeriodLabelContent } from './styles';

type QuickRangeProps = {
  previousPeriods: PreviousPeriods;
  selectedComparisonDateRange: DateRange | null;
  selectedComparisonBaseline: number | null;
  onQuickRangeChange: (dates: [Date, Date]) => void;
  onOpenBaselineYearPicker: () => void;
  onClearComparison: () => void;
};

export const QuickRangeSelections = ({
  previousPeriods,
  selectedComparisonDateRange,
  selectedComparisonBaseline,
  onQuickRangeChange,
  onOpenBaselineYearPicker,
  onClearComparison,
}: QuickRangeProps) => {
  const { t } = useTranslation();

  const { previousPeriodStart, previousPeriodEnd, previousYearStart, previousYearEnd } =
    previousPeriods;

  const previousYearIsDuplicateOfPreviousPeriod = isSameRange(
    { startDate: previousPeriodStart, endDate: previousPeriodEnd },
    previousYearStart,
    previousYearEnd,
  );

  // Format labels for the buttons
  const previousPeriodStartLabel = dayjs(previousPeriodStart).format('YYYY-MM');
  const previousPeriodEndLabel = dayjs(previousPeriodEnd).format('YYYY-MM');
  const previousYearStartLabel = dayjs(previousYearStart).format('YYYY-MM');
  const previousYearEndLabel = dayjs(previousYearEnd).format('YYYY-MM');

  const previousPeriodTitle = t('comparisonpicker.previousPeriod', 'Previous Period');
  const previousYearTitle = t('comparisonpicker.previousYear', 'Previous Year');

  const isPreviousPeriodSelected = isSameRange(
    selectedComparisonDateRange,
    previousPeriodStart,
    previousPeriodEnd,
  );
  const isPreviousYearSelected = isSameRange(
    selectedComparisonDateRange,
    previousYearStart,
    previousYearEnd,
  );

  return (
    <QuickRangeWrapper>
      <PeriodButton
        selected={isPreviousPeriodSelected}
        aria-pressed={isPreviousPeriodSelected}
        onClick={() => {
          if (isPreviousPeriodSelected) {
            onClearComparison();

            return;
          }

          onQuickRangeChange([previousPeriodStart, previousPeriodEnd]);
        }}
      >
        <PeriodLabelContent>
          <span>{`${previousPeriodTitle}: ${previousPeriodStartLabel}`}</span>
          <ArrowRight width="12px" height="12px" />
          <span>{previousPeriodEndLabel}</span>
        </PeriodLabelContent>
      </PeriodButton>

      {!previousYearIsDuplicateOfPreviousPeriod && (
        <PeriodButton
          selected={isPreviousYearSelected}
          aria-pressed={isPreviousYearSelected}
          onClick={() => {
            if (isPreviousYearSelected) {
              onClearComparison();

              return;
            }

            onQuickRangeChange([previousYearStart, previousYearEnd]);
          }}
        >
          <PeriodLabelContent>
            <span>{`${previousYearTitle}: ${previousYearStartLabel}`}</span>
            <ArrowRight width="12px" height="12px" />
            <span>{previousYearEndLabel}</span>
          </PeriodLabelContent>
        </PeriodButton>
      )}
      <PeriodButton
        selected={selectedComparisonBaseline !== null}
        onClick={() => {
          if (selectedComparisonBaseline !== null) {
            onClearComparison();

            return;
          }

          onOpenBaselineYearPicker();
        }}
      >
        {t('comparisonpicker.baseline', 'Baseline')}
      </PeriodButton>
    </QuickRangeWrapper>
  );
};
